import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { TcpaConsentLink, TcpaConsentLinkStatus } from 'projects/borrower-app/src/app/models/tcpa-consent-link.model';
import { AnonymousPortalSettingsService } from 'projects/borrower-app/src/app/services/anonymous-portal-settings.service';
import { NavigationService } from 'projects/borrower-app/src/app/services/navigation.service';
import { TcpaConsentLinkService } from 'projects/borrower-app/src/app/services/tcpa-consent-link.service';
import { UtilsService } from 'projects/borrower-app/src/app/services/utils.service';

/**
 * LLS-1039: TCPA consent from a link in a campaign email (`/tcpa-consent?companyGuid=...&messageGuid=...`).
 * Opening the page only shows the consent text; consent is recorded when the recipient ticks the box and submits.
 */
@Component({
  selector: 'tcpa-consent-link',
  templateUrl: 'tcpa-consent-link.component.html',
  standalone: false
})
export class TcpaConsentLinkComponent implements OnInit, OnDestroy {

  status: TcpaConsentLinkStatus | 'Loading' | 'Error' = 'Loading';
  link: TcpaConsentLink | null = null;
  agreed = false;
  isSubmitting = false;
  submitFailed = false;
  consentChanged = false;
  isRefreshing = false;
  possibleLogoUrls: string[] = [];

  private _companyGuid = '';
  private _messageGuid = '';
  private _subscriptions = new Subscription();

  constructor(private readonly _tcpaConsentLinkService: TcpaConsentLinkService,
    private readonly _utilsService: UtilsService,
    private readonly _navigationService: NavigationService,
    private readonly _translocoService: TranslocoService,
    protected readonly anonymousPortalSettings: AnonymousPortalSettingsService) {
    this.possibleLogoUrls = this._navigationService.possibleLogoUrls;
  }

  /** The text in the language the page is shown in, falling back to English when the company has no Spanish text. */
  get consentText(): string | null {
    if (this._translocoService.getActiveLang() === 'es' && this.link?.consentTextSpanish) {
      return this.link.consentTextSpanish;
    }
    return this.link?.consentText ?? null;
  }

  ngOnInit(): void {
    this._companyGuid = this._utilsService.getUrlParameter('companyGuid');
    this._messageGuid = this._utilsService.getUrlParameter('messageGuid');
    this.anonymousPortalSettings.ensureLoaded(this._companyGuid);

    if (!this._companyGuid || !this._messageGuid) {
      this.status = 'Unavailable';
      return;
    }

    this.loadConsentLink();
  }

  private loadConsentLink(): void {
    // The form stays disabled until the terms arrive, and any agreement is cleared when they do, so a box ticked
    // against earlier terms can never be submitted against new ones.
    this.isRefreshing = true;
    this._subscriptions.add(this._tcpaConsentLinkService.getConsentLink(this._companyGuid, this._messageGuid).subscribe({
      next: link => {
        this.agreed = false;
        this.link = link;
        this.status = link.status;
        this.isRefreshing = false;
      },
      error: () => {
        this.status = 'Error';
        this.isRefreshing = false;
      },
    }));
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  onSubmitClicked(): void {
    if (!this.agreed || this.isSubmitting || this.isRefreshing) {
      return;
    }

    // Record the language the text was actually shown in, so the server stores that copy.
    const languageCode = this.consentText === this.link?.consentTextSpanish && this.link?.consentTextSpanish ? 'es' : 'en';

    this.isSubmitting = true;
    this.submitFailed = false;
    this.consentChanged = false;
    this._subscriptions.add(this._tcpaConsentLinkService.submitConsent(this._companyGuid, this._messageGuid, languageCode, this.link?.consentVersion).subscribe({
      next: result => {
        this.status = result.status;
        this.isSubmitting = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isSubmitting = false;
        if (error?.status === 409) {
          // The terms or phone changed since the page loaded: show the current ones and ask for consent again.
          this.consentChanged = true;
          this.loadConsentLink();
          return;
        }
        this.submitFailed = true;
      },
    }));
  }
}
